import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CaptureActivityPlayer from '@/components/studies/CaptureActivityPlayer';
import { Clock, Sparkles } from 'lucide-react';

// FASE QUIZZES · PASO 5D-2 — Adapter delgado para ejecutar una actividad
// `capture` dentro del Quiz del alumno.
//
// CONTRATO (idéntico a QuizPuzzleRunner / QuizPlayComputerRunner):
//   { activity, position, totalActivities, quizTitle,
//     maxPoints, maxAttempts, initialAttemptsUsed,
//     onCompleted, onAttemptFailed }
//
// REGLAS 5D-2:
//   · 1 intento por actividad (Capture es libre — los movimientos ilegales
//     NO consumen intento). Header muestra "Intento 1 de 1".
//   · Puntuación: round((captured / total) * maxPoints). División por cero → 0.
//   · Victoria (captura todo)  → onCompleted({success:true,  points_earned:maxPoints}).
//   · Timeout                  → onCompleted({success:false, points_earned:proporcional}).
//   · NUNCA se llama a /classrooms/... — la persistencia va por
//     onCompleted → QuizStart → POST /quiz-assignments/{aid}/progress.
//
// NO se modifica CaptureActivityPlayer. Se monta con `inline=true` y se
// inyecta un `started_at` local + `duration_seconds` en un clon del activity
// para activar su timer interno sin mutar el snapshot del Quiz.

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export default function QuizCaptureRunner({
  activity, position, totalActivities, quizTitle,
  maxPoints = 30, /* maxAttempts intencionalmente ignorado: Capture es 1-shot */
  initialAttemptsUsed = 0,
  onCompleted, /* onAttemptFailed no aplica en Capture */
}) {
  // Refs / estado local del wrapper.
  const startedAtIsoRef = useRef(null);            // ISO de arranque LOCAL (no persistido)
  const startedAtMsRef = useRef(null);
  const finishedRef = useRef(false);               // idempotencia: 1 sola finalización
  const latestRef = useRef({ captured: 0, total: 0, completed: false });
  const [seconds, setSeconds] = useState(0);
  const [remaining, setRemaining] = useState(0);

  // Detectar capture_config inválido/vacío ANTES de montar el Player.
  const hasValidConfig = useMemo(() => {
    const cfg = activity?.capture_config;
    if (!cfg || !Array.isArray(cfg.pieces)) return false;
    const student = cfg.pieces.find(p => p?.role === 'student');
    const targets = cfg.pieces.filter(p => p?.role === 'target');
    return !!student && targets.length > 0;
  }, [activity]);

  // Total inicial (para calcular puntos aunque el Player mute `pieces`).
  const totalTargets = useMemo(() => {
    const cfg = activity?.capture_config;
    if (!cfg || !Array.isArray(cfg.pieces)) return 0;
    return cfg.pieces.filter(p => p?.role === 'target').length;
  }, [activity]);

  const timeLimit = Number.isFinite(activity?.time_seconds) && activity.time_seconds > 0
    ? Number(activity.time_seconds) : 0;

  // Al montar (o cambiar de actividad): reset locales, fijar started_at.
  useEffect(() => {
    finishedRef.current = false;
    latestRef.current = { captured: 0, total: totalTargets, completed: false };
    startedAtMsRef.current = Date.now();
    startedAtIsoRef.current = new Date(startedAtMsRef.current).toISOString();
    setSeconds(0);
    setRemaining(timeLimit || 0);
  }, [activity?.id, activity?.source_activity_id, totalTargets, timeLimit]);

  const computePoints = useCallback((captured, total) => {
    const t = Number(total) || 0;
    if (t <= 0) return 0;
    return Math.round((Number(captured) || 0) / t * (Number(maxPoints) || 0));
  }, [maxPoints]);

  const finish = useCallback((success) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const { captured, total } = latestRef.current;
    const pts = success
      ? (Number(maxPoints) || 0)       // §Victoria: puntos completos
      : computePoints(captured, total || totalTargets);
    const elapsed = Math.max(0, Math.floor((Date.now() - (startedAtMsRef.current || Date.now())) / 1000));
    try {
      onCompleted?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: (Number(initialAttemptsUsed) || 0) + 1,
        points_earned: pts,
        elapsed_seconds: elapsed,
        success,
        completed: true,
      });
    } catch { /* silent — el padre no debe romper el wrapper */ }
  }, [activity, position, initialAttemptsUsed, maxPoints, computePoints, onCompleted, totalTargets]);

  // Timer visual global (siempre) + countdown si hay time_seconds.
  useEffect(() => {
    if (finishedRef.current || !hasValidConfig) return;
    const iv = setInterval(() => {
      const nowSec = Math.floor((Date.now() - (startedAtMsRef.current || Date.now())) / 1000);
      setSeconds(nowSec);
      if (timeLimit > 0) {
        const left = Math.max(0, timeLimit - nowSec);
        setRemaining(left);
        // §Timeout: finalizar UNA sola vez con puntos proporcionales.
        if (left === 0 && !finishedRef.current && !latestRef.current.completed) {
          finish(false);
        }
      }
    }, 500);
    return () => clearInterval(iv);
  }, [timeLimit, hasValidConfig, finish]);

  // onProgress del Player: registrar último snapshot y detectar victoria.
  const handleProgress = useCallback(({ captured, total, completed }) => {
    latestRef.current = {
      captured: Number(captured) || 0,
      total: Number(total) || totalTargets,
      completed: !!completed,
    };
    // §Victoria: capturó todos los objetivos → 1 sola finalización.
    if (completed && !finishedRef.current) {
      finish(true);
    }
  }, [finish, totalTargets]);

  // Activity parcheado: inyecta started_at + duration_seconds locales para
  // activar el timer interno del Player. NO muta el snapshot original.
  const patchedActivity = useMemo(() => {
    if (!hasValidConfig || !startedAtIsoRef.current) return activity;
    const base = { ...(activity || {}) };
    if (timeLimit > 0) {
      base.duration_seconds = timeLimit;
      base.started_at = startedAtIsoRef.current;
    }
    return base;
  }, [activity, hasValidConfig, timeLimit]);

  // FEN inválido / capture_config ausente → mensaje friendly, sin consumir intento.
  if (!activity) return null;
  if (!hasValidConfig) {
    return (
      <div data-testid="qcap-invalid" className="min-h-[calc(100vh-4rem)] grid place-items-center p-6 bg-gradient-to-b from-indigo-50/40 via-white to-white">
        <div className="max-w-lg text-center rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="text-5xl mb-3">♟️</div>
          <div className="font-display font-black text-xl text-slate-900">Esta actividad no tiene una posición válida.</div>
          <div className="text-sm text-slate-600 mt-2">Avísale a tu profesor para que la revise.</div>
        </div>
      </div>
    );
  }

  const pct = totalActivities > 0 ? Math.round((position / totalActivities) * 100) : 0;

  return (
    <div data-testid="qcap-runner" className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Header idéntico en estilo a los otros runners del Quiz */}
        <div className="mb-4">
          <div data-testid="qcap-title" className="font-display font-black text-xl sm:text-2xl text-slate-900 truncate">🎯 {quizTitle}</div>
          <div className="flex items-center justify-between mt-1 gap-3">
            <div data-testid="qcap-progress-text" className="text-xs sm:text-sm text-slate-600 font-semibold">Actividad {position + 1} de {totalActivities}</div>
            <div className="flex items-center gap-2">
              {timeLimit > 0 ? (
                <div data-testid="qcap-countdown" className={`inline-flex items-center gap-1 text-xs sm:text-sm font-mono ${remaining <= 10 ? 'text-rose-600' : 'text-slate-500'}`}>
                  <Clock className="w-3.5 h-3.5" /> {fmtTime(remaining)}
                </div>
              ) : (
                <div data-testid="qcap-timer" className="inline-flex items-center gap-1 text-xs sm:text-sm font-mono text-slate-400">
                  <Clock className="w-3.5 h-3.5" /> {fmtTime(seconds)}
                </div>
              )}
              <div data-testid="qcap-attempt-badge" className="text-[11px] text-slate-500 font-semibold whitespace-nowrap">
                <Sparkles className="w-3 h-3 inline mr-0.5"/> Intento 1 de 1
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
            <div data-testid="qcap-progress-bar" className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Player original en modo inline. NO se modifica: solo se le pasa un
            activity parcheado con started_at/duration_seconds locales y un
            onProgress que traduce sus eventos al contrato del Quiz. */}
        <div data-testid="qcap-player-mount">
          <CaptureActivityPlayer
            key={`${position}-${activity?.source_activity_id || activity?.id}`}
            open
            inline
            activity={patchedActivity}
            onProgress={handleProgress}
          />
        </div>
      </div>
    </div>
  );
}
